// Strict allowlist for tenant-supplied branding colors.
// Accepted: #rgb #rrggbb #rrggbbaa, rgb()/rgba()/hsl()/hsla()/oklch()/oklab().
// Rejects anything containing url(, var(, semicolons, braces, comments, or
// identifiers other than the function name + optional `deg` unit.
const HEX = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const FN = /^(rgb|rgba|hsl|hsla|oklch|oklab)\(\s*(.+?)\s*\)$/i;
// Per-token inner: a numeric token (optionally suffixed with `deg`) or a
// separator character (comma, percent, slash, whitespace). This permits
// CSS Color Level 4 syntax such as `hsl(120deg 50% 50%)` where `deg` may
// appear after each hue value, not only at the very end of the argument list.
const FN_INNER = /^(?:[0-9.+-]+(?:deg)?|[.,%/\s])+$/;
// Reject ASCII control characters that could break out of the CSS context.
// eslint-disable-next-line no-control-regex -- intentional: reject CSS-breaking control chars in tenant input
const CONTROL = /[\x00-\x1f\x7f;{}\\<>"'`]/;

/**
 * Strict allowlist validator for tenant-supplied branding colors.
 *
 * **Security boundary.** The output is written to the `--sentinel-brand-primary`
 * CSS custom property and interpolated **unquoted** into
 * `color-mix(in oklch, ${sentinel.brandPrimary} 18%, transparent)` in
 * `auth-shell.tsx`. Any value reaching the CSS variable becomes raw CSS, so
 * this allowlist must stay strict — never relax it without a corresponding
 * change to how the value is consumed downstream.
 *
 * Returns the input (trimmed) if accepted; `null` otherwise. Callers MUST
 * fall back to a known-safe default on `null`.
 *
 * Accepted shapes:
 *   - #rgb / #rrggbb / #rgba / #rrggbbaa
 *   - rgb()/rgba()/hsl()/hsla()/oklch()/oklab() with numeric arguments
 *     (plus optional `deg` unit on hue values; `%` allowed on percentage
 *     channels).
 */
export function validateBrandColor(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  const v = input.trim();
  if (v.length === 0 || v.length > 64) return null;
  if (CONTROL.test(v)) return null;
  if (/url\s*\(|var\s*\(|\/\*|\*\//i.test(v)) return null;
  if (HEX.test(v)) return v;
  const m = v.match(FN);
  if (m && FN_INNER.test(m[2]!)) return v;
  return null;
}
